import { useState, useEffect } from "react";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import apiClient from "../Api/apiConfig";

import "../styles/ResetPassword.css";

export default function ResetPassword() {
  const [email, setEmail] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [successMessage, setSuccessMessage] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  const [showPopup, setShowPopup] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const navigate = useNavigate();
  const { token } = useParams();
  const location = useLocation();

  useEffect(() => {
    if (!token) {
      setErrorMessage("❌ Token de réinitialisation manquant ou invalide.");
    }

    // Récupérer l'email passé en query string (si présent)
    const searchParams = new URLSearchParams(location.search);
    const emailFromQuery = searchParams.get("email");
    if (emailFromQuery) {
      setEmail(emailFromQuery);
    }
  }, [token, location.search]);

  // 🔐 Validation du formulaire
  const validateForm = () => {
    if (!email || !newPassword || !confirmPassword) {
      setErrorMessage("❌ Tous les champs sont requis.");
      return false;
    }

    if (newPassword !== confirmPassword) {
      setErrorMessage("❌ Les mots de passe ne correspondent pas.");
      return false;
    }

    // Min 8 chars, 1 maj, 1 chiffre, 1 spécial
    const passwordRegex =
      /^(?=.*[A-Z])(?=.*\d)(?=.*[!@#$%^&*()_+])[A-Za-z\d!@#$%^&*()_+]{8,}$/;

    if (!passwordRegex.test(newPassword)) {
      setErrorMessage(
        "❌ Le mot de passe doit contenir au moins 8 caractères, une majuscule, un chiffre et un caractère spécial."
      );
      return false;
    }

    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSuccessMessage("");
    setErrorMessage("");

    if (!validateForm()) return;

    setIsSubmitting(true);

    try {
      const { data } = await apiClient.post("/users/reset-password", {
        email: email.trim(),
        resetPasswordToken: token,
        newPassword,
      });

      setSuccessMessage(
        data.message || "✅ Votre mot de passe a été réinitialisé avec succès."
      );
      setShowPopup(true);

      setTimeout(() => {
        navigate("/login");
      }, 3000);
    } catch (error) {
      console.error("Reset Password Error:", error);
      const msg =
        error.response?.data?.error ||
        error.response?.data?.message ||
        error.message ||
        "❌ Une erreur technique est survenue.";
      setErrorMessage(msg);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="reset-password-page">
      <div className="reset-password-container">
        <h2>Réinitialisation du mot de passe</h2>

        <form onSubmit={handleSubmit} className="reset-password-form">
          <div className="form-group">
            <label htmlFor="email">Adresse email</label>
            <input
              type="email"
              id="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="Confirmez votre email"
              required
              disabled={isSubmitting}
            />
          </div>

          <div className="form-group">
            <label htmlFor="newPassword">Nouveau mot de passe</label>
            <input
              type="password"
              id="newPassword"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              placeholder="Votre nouveau mot de passe"
              required
              disabled={isSubmitting}
            />
          </div>

          <div className="form-group">
            <label htmlFor="confirmPassword">Confirmez le mot de passe</label>
            <input
              type="password"
              id="confirmPassword"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              placeholder="Répétez le mot de passe"
              required
              disabled={isSubmitting}
            />
          </div>

          {errorMessage && <div className="error-message">{errorMessage}</div>}
          {successMessage && <div className="success-message">{successMessage}</div>}

          <button type="submit" className="submit-button" disabled={isSubmitting}>
            {isSubmitting ? "Traitement..." : "Réinitialiser mon mot de passe"}
          </button>
        </form>
      </div>

      {showPopup && (
        <div className="popup-overlay">
          <div className="popup-content">
            <div className="popup-icon">🎉</div>
            <h3>Succès !</h3>
            <p>Votre mot de passe a été mis à jour.</p>
            <p>Vous allez être redirigé vers la page de connexion.</p>
            <button onClick={() => navigate("/login")} className="close-button">
              Connexion immédiate
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
